import {
	extractAdditionalLabels,
	extractCoreLabels
} from 'src/apps/dashboard/helpers/ConfigurationProcessingHelpers';
import { API_URL_PATHS } from 'src/common/consts/ApiUrlPaths';
import { apiCall } from 'src/common/services/api/ServerApi';
import { Logger } from 'src/common/services/Logger';
import { ConfigurationLabel, ConfigurationLabelValue } from 'src/common/types/ConfigurationTypes';

import { Label, LabelType, LabelValue } from '../../pages/label-management/types';

const { CENTRAL_CONFIG_PATH } = API_URL_PATHS;

const mapConfigValueToLabelValue = (value: string | ConfigurationLabelValue): LabelValue => {
	if (typeof value === 'string') {
		return { value, dashboardVisible: true };
	}

	return {
		value: value.value,
		dashboardVisible: !(value.excludedFromDashboard ?? false)
	};
};

const mapConfigLabelToLabel = (configLabel: ConfigurationLabel, type: LabelType): Label => ({
	key: configLabel.label,
	displayName: configLabel.displayName ?? configLabel.label,
	description: configLabel.description ?? '',
	type,
	values: configLabel.values.map(mapConfigValueToLabelValue).filter((lv) => lv.value)
});

const mapLabelValueToConfigValue = (labelValue: LabelValue): string | ConfigurationLabelValue => {
	const { value, dashboardVisible } = labelValue;
	if (dashboardVisible === false) {
		return { value, excludedFromDashboard: true };
	}

	return value;
};

const mapLabelToConfigLabel = (label: Label): ConfigurationLabel => ({
	label: label.key,
	displayName: label.displayName,
	...(label.description ? { description: label.description } : {}),
	values: label.values.map(mapLabelValueToConfigValue)
});

export const fetchLabels = async (productId: string): Promise<Label[]> => {
	try {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const config: any = await apiCall('GET', `${CENTRAL_CONFIG_PATH}?productId=${productId}`, {
			isConfigUrl: true
		});

		const platform = config?.PLATFORM ?? {};
		const coreLabels = extractCoreLabels(platform).map((l) =>
			mapConfigLabelToLabel(l, LabelType.CORE)
		);
		const additionalLabels = extractAdditionalLabels(platform).map((l) =>
			mapConfigLabelToLabel(l, LabelType.CUSTOM)
		);

		return [...coreLabels, ...additionalLabels];
	} catch (error) {
		Logger.error('labelManagementService -> fetchLabels failed:', error);
		throw error;
	}
};

export const saveLabel = async (productId: string, label: Label): Promise<void> => {
	try {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const config: any = await apiCall('GET', `${CENTRAL_CONFIG_PATH}?productId=${productId}`, {
			isConfigUrl: true
		});

		const platform = config?.PLATFORM ?? {};
		const configKey = label.type === LabelType.CORE ? 'CORE_LABELS' : 'ADDITIONAL_LABELS';
		const labelsArray: ConfigurationLabel[] = platform[configKey] ?? [];

		const existingIndex = labelsArray.findIndex((configLabel) => configLabel.label === label.key);
		const updatedLabelsArray =
			existingIndex >= 0
				? labelsArray.map((configLabel, i) =>
						i === existingIndex ? mapLabelToConfigLabel(label) : configLabel
					)
				: [...labelsArray, mapLabelToConfigLabel(label)];

		const updatedConfig = {
			...config,
			PLATFORM: {
				...platform,
				[configKey]: updatedLabelsArray
			}
		};

		await apiCall('POST', `${CENTRAL_CONFIG_PATH}?productId=${productId}`, {
			isConfigUrl: true,
			data: updatedConfig
		});
	} catch (error) {
		Logger.error('labelManagementService -> saveLabel failed:', error);
		throw error;
	}
};

export const deleteLabel = async (productId: string, labelKey: string): Promise<void> => {
	try {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const config: any = await apiCall('GET', `${CENTRAL_CONFIG_PATH}?productId=${productId}`, {
			isConfigUrl: true
		});

		const platform = config?.PLATFORM ?? {};
		const labelsArray: ConfigurationLabel[] = platform.ADDITIONAL_LABELS ?? [];
		const updatedLabelsArray = labelsArray.filter((configLabel) => configLabel.label !== labelKey);

		const updatedConfig = {
			...config,
			PLATFORM: {
				...platform,
				ADDITIONAL_LABELS: updatedLabelsArray
			}
		};

		await apiCall('POST', `${CENTRAL_CONFIG_PATH}?productId=${productId}`, {
			isConfigUrl: true,
			data: updatedConfig
		});
	} catch (error) {
		Logger.error('labelManagementService -> deleteLabel failed:', error);
		throw error;
	}
};
